import { useEffect, useState } from "react";
import { Card } from "../../components/Card";
import { Button } from "../../components/Button";
import { api } from "../../lib/api";
import { monthsBetween } from "../../lib/format";
import type { SubscriptionDTO } from "@shared/api";

interface Props {
  sub: SubscriptionDTO;
  onSaved: () => void | Promise<void>;
}

const PRESETS = [6, 12, 24, 36, 48];

function addMonths(iso: string, months: number): string {
  const d = new Date(iso);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

export function TermsManager({ sub, onSaved }: Props) {
  const [start, setStart] = useState(sub.termStart);
  const [end, setEnd] = useState(sub.termEnd);
  const [autoRenew, setAutoRenew] = useState(sub.autoRenew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setStart(sub.termStart);
    setEnd(sub.termEnd);
    setAutoRenew(sub.autoRenew);
  }, [sub.id, sub.termStart, sub.termEnd, sub.autoRenew]);

  const termMonths = monthsBetween(start, end);
  const dirty =
    start !== sub.termStart ||
    end !== sub.termEnd ||
    autoRenew !== sub.autoRenew;

  const setMonths = (m: number) => setEnd(addMonths(start, m));

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.subscriptions.update(sub.id, {
        termStart: start,
        termEnd: end,
        autoRenew,
      });
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bbc-detail-grid bbc-detail-grid-1-2">
      <Card
        title="Term length"
        action={
          <div className="bbc-card-actions">
            <Button
              variant="primary"
              size="sm"
              onClick={save}
              disabled={!dirty || saving}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        }
      >
        {error && <div className="bbc-error">{error}</div>}
        <div className="bbc-term-display">
          <div className="bbc-term-months mono">{termMonths}</div>
          <div className="bbc-term-unit">months</div>
        </div>
        <div className="bbc-term-dates">
          <label>
            <span>Start</span>
            <input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </label>
          <label>
            <span>End</span>
            <input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
          </label>
        </div>
        <div className="bbc-term-presets">
          <span>Quick set:</span>
          {PRESETS.map((m) => (
            <button
              key={m}
              type="button"
              className={termMonths === m ? "is-on" : ""}
              onClick={() => setMonths(m)}
            >
              {m} mo
            </button>
          ))}
        </div>
        <div className="bbc-term-toggle">
          <label>
            <input
              type="checkbox"
              checked={autoRenew}
              onChange={(e) => setAutoRenew(e.target.checked)}
            />
            <span>Auto-renew at term end</span>
          </label>
          <p>
            If on, the subscription will renew for an additional {termMonths}{" "}
            months at the prevailing rate for connected services. 90 days
            notice required to opt out.
          </p>
        </div>
      </Card>

      <Card title="IP rights & contract">
        <div className="bbc-ip-grid">
          <div>
            <div className="bbc-ip-label">Distribution model</div>
            <div className="bbc-ip-value">{sub.ipScope}</div>
          </div>
          <div>
            <div className="bbc-ip-label">Geographic scope</div>
            <div className="bbc-ip-value">
              Global, excluding sanctioned territories
            </div>
          </div>
          <div>
            <div className="bbc-ip-label">Field of use</div>
            <div className="bbc-ip-value">
              {sub.domain} solutions; downstream customer deployments
              permitted
            </div>
          </div>
          <div>
            <div className="bbc-ip-label">Sublicensing</div>
            <div className="bbc-ip-value">
              Permitted to direct end customers; written notice required for
              resellers
            </div>
          </div>
          <div>
            <div className="bbc-ip-label">Royalty model</div>
            <div className="bbc-ip-value">
              Flat-rate annual + per-call overage above 10M / month
            </div>
          </div>
          <div>
            <div className="bbc-ip-label">Attestation cadence</div>
            <div className="bbc-ip-value">
              Quarterly usage report due within 30 days of quarter end
            </div>
          </div>
        </div>

        <div className="bbc-doc-list">
          <h5>Contract documents</h5>
          {[
            {
              name: `MSA-${sub.code}-2026.pdf`,
              meta: "Signed Jan 14, 2026 · 24 pages",
            },
            {
              name: `IP-Schedule-${sub.code}.pdf`,
              meta: "Signed Jan 14, 2026 · 6 pages",
            },
            {
              name: `DPA-${sub.code}.pdf`,
              meta: "Signed Jan 14, 2026 · 11 pages",
            },
          ].map((d) => (
            <div key={d.name} className="bbc-doc-row">
              <div>
                <div className="bbc-doc-name mono">{d.name}</div>
                <div className="bbc-doc-meta">{d.meta}</div>
              </div>
              <button type="button" className="bbc-text-btn-sm">
                Download
              </button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
